// Pure data. The contract shared by the parser, tagger, ledger, and GameBoard.
// No runtime dependencies; safe to import from anywhere.

export const TRIGGER = Object.freeze({
  ON_SURVIVE_DAMAGE: 'on_survive_damage',
  WOULD_BE_SUMMONED: 'would_be_summoned',
  ON_PLAY:         'on_play',          // Genesis → (SELF entering)
  ON_DEATH:        'on_death',         // Deathrite → (SELF dying)
  ON_TURN_START:   'on_turn_start',
  ON_TURN_END:     'on_turn_end',
  STATIC:          'static',           // continuous while in play
  ACTIVATED:       'activated',        // player-fired (v1.2+)
  // v1.42. Watcher triggers. Fire on OTHER cards when ANY card
  // enters/leaves the realm. Canonical examples:
  //   ON_REALM_ENTRY. Bottomless Pit kills non-Airborne entrants;
  //                    Mariner's Curse submerges aquatic entrants.
  //   ON_REALM_EXIT. Cards that react to other cards leaving play.
  //                    Distinct from ON_DEATH which fires on the
  //                    dying card itself (Deathrite).
  // Watcher entries receive a watchTarget context referencing the
  // card that entered/left so the watcher's condition + effect can
  // reference it. Parser support pending. No automation JSON
  // currently emits these. Infrastructure exists so a future tagger
  // pass over cards like Bottomless Pit just works.
  ON_REALM_ENTRY:  'on_realm_entry',
  ON_REALM_EXIT:   'on_realm_exit',
  // v1.63. Combat triggers. Fired by the CombatEngine resolution
  // (via the host adapter's fireTrigger) at the appropriate points
  // in the strike sequence.
  //   ON_STRIKE. SELF lands a strike (attacker side OR defender
  //                      strike-back). Fires regardless of damage.
  //                      codex: "even if the strike is for 0 damage".
  //                      Watch context: { defender, projectile }.
  //   ON_DAMAGE_DEALT. SELF dealt > 0 damage to another card. Watch
  //                      context: { defender, amount }. Distinct from
  //                      ON_STRIKE: 0-damage strikes don't fire this.
  //   ON_DAMAGE_TAKEN. SELF received > 0 damage. Watch context:
  //                      { source, amount }. Fires once per damage
  //                      instance (Ward absorbing drops amount to 0,
  //                      which skips the trigger).
  //   ON_ATTACK. SELF declared an attack (regardless of
  //                      whether it lands; fires once when target
  //                      gets picked). Watch context: { defender }.
  //   ON_KILL. SELF killed a Minion (currentLife → 0).
  //                      Watch context: { killed }. Fires after the
  //                      death sweep notices the kill.
  // Parser support is incremental. These constants are wired into
  // the runtime; tagger / parser additions land per-card as needed.
  ON_STRIKE:        'on_strike',
  ON_DAMAGE_DEALT:  'on_damage_dealt',
  ON_DAMAGE_TAKEN:  'on_damage_taken',
  ON_ATTACK:        'on_attack',
  ON_KILL:          'on_kill',
  // v1.77. Replacement-effect triggers. Codex: replacement effects
  // do NOT use the storyline; they fire immediately when the "would"
  // event is about to occur. The runtime queries every active
  // would-* trigger before committing the underlying event. Examples:
  //   WOULD_DIE. Gilded Aegis (heal + banish self), Lady
  //                       Iseult (return ally to hand), Altar of
  //                       Malachai (sacrifice instead).
  //   WOULD_TAKE_DAMAGE. Tufted Turtles (prevent first damage each
  //                       turn), Goswhit Helmet (prevent strike +
  //                       return self to hand), Royal Bodyguard
  //                       (redirect damage from royalty).
  // Watch context carries { card, source, amount } so the
  // replacement effect can condition / target accordingly. Parser
  // support per-card lands incrementally.
  WOULD_DIE:         'would_die',
  WOULD_TAKE_DAMAGE: 'would_take_damage',
  // Fires on the RECEIVER when a unit enters its cell. Distinct from
  // ON_REALM_ENTRY (any card enters the realm, fired on other cards)
  // because ON_ENTER_LOCATION is movement-step scoped and fires on
  // every card currently AT the entered cell. Druid's back-face aura
  // grants this trigger to nearby allied sites via
  // `grant_trigger_to_scope`; a future intrinsic "this site damages
  // enemies that enter" card would carry the entry on its own
  // automation list. Watch context: { target }. The entering unit.
  ON_ENTER_LOCATION: 'on_enter_location',
  // Fires on the MOVING unit itself when it completes a move
  // (voluntary step, teleport, pull, push. Every path funnels
  // through fireOnEnterLocationAt). Summons are NOT stops. Distinct
  // from ON_ENTER_LOCATION, which fires on the cards AT the
  // destination and excludes the mover. Adtonitum: "Whenever
  // Adtonitum stops, she may deal 3 damage to another random unit
  // there."
  ON_STOP: 'on_stop',
  // Fires on every card in play when a player casts a Magic spell.
  // Scope `self_controller` on the watcher entry narrows it to the
  // caster's own cards (Enchantress's "whenever YOU cast"). Watch
  // context: `{ target }`. The spell card that was just cast, before
  // it's routed to cemetery. Distinct from ON_PLAY which fires on the
  // spell's own card for its own effects; ON_SPELL_CAST is the bystander
  // reaction pipeline.
  ON_SPELL_CAST: 'on_spell_cast',
  // Fires on a wall (INTERSECTION_2 aura) when a unit crosses the
  // edge the wall sits on. Watch context: { target }. The crossing
  // unit. Distinct from ON_ENTER_LOCATION (cell-level entry). The
  // traverse trigger is edge-level and fires regardless of whether
  // the cell on the far side has any other entry listeners.
  ON_EDGE_TRAVERSE: 'on_edge_traverse',
  // Fires on EVERY card in play (both players' cards) at the end of
  // each player's turn. Distinct from ON_TURN_END which only fires on
  // the active player's cards. Cursed Iron pattern: "Artifacts here
  // each deal 1 damage to its bearer at the end of each turn." The
  // damage should fire on the opponent's end-of-turn too.
  AFTER_EACH_TURN: 'after_each_turn',
  // Fires on EVERY card in play once per card drawn, from the single
  // draw choke point (host.drawCard). Covers BOTH the mandatory
  // start-phase draw and effect-driven draws. Watch context:
  // `{ player }`. The player who drew. `scope: 'self_controller'`
  // narrows to the drawer's own cards. The runtime guards re-entrancy
  // so an on_draw effect that itself draws (Queen of Midland) doesn't
  // re-fire on_draw. Iron Maiden ("nearby Avatar draws → lose life
  // equal to cards drawn this turn") and Chains of Prometheus ("a
  // player draws → tap their strongest untapped minion") use this.
  ON_DRAW: 'on_draw',
  // Fires on EVERY card in play once per minion tap, from the single
  // tap choke point (host._applyTap, nextValue === true only). Watch
  // context: `{ target }`. The minion that just tapped. Distinct from
  // ON_DRAW's watcher fan-out only in the triggering event. The runtime
  // guards re-entrancy so an on_tap effect that itself taps doesn't
  // re-fire on_tap. False Idol ("Whenever a nearby minion taps, its
  // opponent draws a spell") uses this with watch_target_filter
  // (near_source, Minion) + draw_for_watch_target_opponent.
  ON_TAP: 'on_tap',
  // Fires on watcher cards when a minion is SUMMONED onto a specific
  // cell. The watcher's own cell (own mesh OR, for an equipped
  // Monument, its host site's cell). Distinct from ON_ENTER_LOCATION
  // (fires on every card at the cell for any entry path) and ON_STOP
  // (fires on the mover, never on summon). The fresh-summon commit
  // resolves the summon cell and fires this on every watcher anchored
  // there. Watch context: `{ target }`. The summoned minion, so
  // watch_target_filter (name / name_words / subtype) gates it. The
  // Round Table ("Whenever you summon King Arthur, or a Knight, Sir, or
  // Dame to The Round Table, draw a card") uses this with
  // scope: self_controller + watch_target_filter(name / name_words).
  ON_SUMMON_HERE: 'on_summon_here',
  // Fires on a carriable artifact (Relic) the moment it becomes
  // carried. I.e. when it is equipped onto a Minion or Avatar. The
  // single equip choke point (equipActions.equipCardTo) fires this on
  // the artifact right after the attach commits and its equipped
  // continuous effects are applied. Distinct from ON_PLAY (Genesis,
  // fires once when the artifact enters the realm regardless of where
  // it lands). A Relic dropped on the field and later picked up has
  // already fired its Genesis but fires ON_PICKUP each time it's
  // carried. 13 Treasures of Britain: "When picked up, bearer's
  // controller draws a card." scope: self_controller; the bearer's
  // controller is threaded in as the effective controller.
  ON_PICKUP: 'on_pickup',
  // Fires on a subsurface artifact (a buried / submerged Relic) the
  // moment it is carried up to the surface. The single subsurface
  // choke point (cardStateOps.setSubsurface) fires this on the card
  // when its region transitions from underground/underwater to
  // surface (region === null). Distinct from ON_STOP (cell-level move
  // completion). Surfacing is a region transition, not a cell move.
  // Buried Treasure / Sunken Treasure: "When carried to the surface,
  // its controller sacrifices it and draws two cards." scope:
  // self_controller.
  ON_SURFACE: 'on_surface',
});

export const OP = Object.freeze({
  GRANT_THRESHOLD:        'grant_threshold',        // args: { air?, earth?, fire?, water? }
  GRANT_MANA:             'grant_mana',             // args: { amount }
  DRAW:                   'draw',                   // args: { amount, source: 'spell' | 'site' | 'card' }
  // source:'card' = the codex Draw default ("draw a card"): the drawer picks
  // spellbook or atlas per card via a server modal (drawSourceOptions).
  // Pick an allied minion; it draws a spell that only it can cast
  // ("Only It Can Cast"). The drawn copy is tagged _castableOnlyBy the
  // picked minion's instance id and drawn for that minion's controller.
  // args: { source?: 'spell' | 'site', target }. Archangel Gabriel.
  DRAW_OWNER_LOCKED_TO_TARGET: 'draw_owner_locked_to_target',
  // Player picks one of `options` (element keys); on confirm, the
  // chosen element is granted at the entry's duration. Used for
  // Valley of Delight ("Choose one: A, E, F, W. Provides that
  // permanently").
  CHOICE_GRANT_THRESHOLD: 'choice_grant_threshold', // args: { options: ['air',...], amount }
  // Continuous power modifier applied to other cards in scope.
  // args: { delta, filter: { controller?, type?, subtype? }, exclude_self? }
  // Resolved against the board on every play/remove/move/passTurn.
  // Silent. No prompt; modifies the affected cards' lifeHUD directly.
  GRANT_STAT:             'grant_stat',
  // Continuous keyword grant. Adds a STATUS_EFFECTS keyword (airborne,
  // burrowing, submerge, etc.) to cards in scope. Same scope+filter
  // resolution as GRANT_STAT. Silent. Manifests via the existing
  // status badge system. Aura-granted statuses live in card._auraStatuses
  // and union with the manually-set card.statuses for rendering.
  CAST_FROM_PILE:         'cast_from_pile',
  MODIFY_HEALING:         'modify_healing',
  MODIFY_RANGE:           'modify_range',
  PREVENT_SITE_CHANGES:   'prevent_site_changes',
  GRANT_KEYWORD:          'grant_keyword',           // args: { keyword: 'burrowing', filter, exclude_self }
  // Wiccan Tools secondary clause. THIS TURN the bearer (the minion
  // carrying the Artifact) gains Spellcaster AND its spells require no
  // threshold. Both grants are per-bearer and revert at the controller's
  // turn end. No args. The bearer is resolved from the firing source.
  GRANT_SPELLCASTER_NO_THRESHOLD_THIS_TURN: 'grant_spellcaster_no_threshold_this_turn',
  // Self-buff that scales with the count of cards matching `count.filter`
  // (name / type / subtype) within `count.scope` (nearby / adjacent /
  // global / here). Source gains `delta × count` power on both halves.
  // Grey Wolves pattern: "Has +1 power for each other Grey Wolves nearby".
  GRANT_STAT_PER_EACH_SELF: 'grant_stat_per_each_self', // args: { delta, count: { filter, scope, exclude_self } }
  // Player life adjustments. Fire instantly on confirm, no ledger
  // entry. Black Obelisk's "lose 2 life and gain ② this turn" packs
  // a LOSE_LIFE alongside a GRANT_MANA inside one entry's effect[].
  LOSE_LIFE:              'lose_life',              // args: { amount }
  GAIN_LIFE:              'gain_life',              // args: { amount }
  // ── Addressed verbs (the AUTHORABLE form) ─────────────────────────
  // One op per verb; `args.select` picks WHO it hits:
  //   select: { mode, ...filter fields (scope/type/subtype/controller/
  //             status/element/exclude_self/any_of/filter),
  //             location? (at_location only) }
  //   mode: 'pick'        → player clicks one match (halo flow)
  //         'all'         → every match, no clicking
  //         'random'      → one seeded-random match
  //         'watch'       → the trigger context supplies the addressee
  //         'self'        → the source card itself
  //         'at_location' → player picks a CELL, hits every match there
  // Sidecar entries use ONLY these; desugarAutomationEntry (below)
  // expands them at load into the internal per-mode ops the dispatch
  // pipelines run on. New sidecar authoring MUST NOT use the internal
  // names. The sidecar schema gate enforces AUTHORABLE_OPS.
  DAMAGE:         'damage',         // args: { amount, select }
  KILL:           'kill',           // args: { select }
  TELEPORT:       'teleport',       // args: { select, destination? }
  RETURN_TO_HAND: 'return_to_hand', // args: { select }
  FORCE_REGION: 'force_region', // args: { select, region, occupants? }
  FORCE_REGION_TARGET: 'force_region_target',
  REARRANGE_SITES: 'rearrange_sites', // args: { region } (2 by 2, then force region)
  BANISH:         'banish',         // args: { select, draw_per? }
  GRANT_STATUS:   'grant_status',   // args: { keyword, select }
  // Single-target effects. Fire once a player clicks a card matching
  // `target` (filter: { scope, type?, subtype?, controller?, status? }).
  // Cancel via ESC or clicking off a valid target.
  KILL_TARGET:            'kill_target',           // args: { target }
  DAMAGE_TARGET:          'damage_target',         // args: { amount, target }
  RETURN_TO_HAND_TARGET:  'return_to_hand_target', // args: { target }
  BANISH_TARGET:          'banish_target',         // args: { target }
  // Multi-target iteration. Applies the op to every card matching
  // `each` filter, no clicking required. `each` shape mirrors target:
  // { scope, type?, subtype?, controller?, status? }.
  KILL_EACH:              'kill_each',             // args: { each }
  DAMAGE_EACH:            'damage_each',           // args: { amount, each }
  RETURN_EACH_TO_HAND:    'return_each_to_hand',   // args: { each }
  // Grant a status keyword to a single picked target (Ward / Stealth /
  // etc.) via the same halo-and-click flow as KILL_TARGET.
  GRANT_STATUS_TO_TARGET: 'grant_status_to_target', // args: { keyword, target }
  // Two-step location targeting. Player picks a CELL (constrained by
  // `location.scope` or `location.distance`), then the op iterates every
  // unit in that cell matching `target.filter`. Ancient Dragon, Vikings,
  // Midland Army pattern. Cell-pick UI lives in GameBoard alongside the
  // existing card-target halos.
  // args: { amount, location: { scope?, distance? }, target: { filter?, exclude_self? }, random?, fx? }
  // random:true damages one uniformly chosen eligible unit; fx:"lightning"
  // publishes the corresponding presentation event. Magic may use scope:"realm".
  DAMAGE_EACH_AT_LOCATION: 'damage_each_at_location',
  // Continuous control claim. While the source is in play, every card
  // matching `filter` is controlled by the source's player. When the
  // source leaves or becomes disabled the claim is released and each
  // affected card reverts to its original controller.
  // args: { filter: { subtype?, type? } }
  // Returned King pattern: "You control all Undead."
  GRANT_CONTROL: 'grant_control',
  // Summon N copies of a named token at every cell matching `location`
  // (scope: 'adjacent' | 'nearby', distance optional). Only cells that
  // currently contain a Site are valid spawn destinations unless the
  // token has the Voidwalk keyword. Used by Midland Army's Deathrite
  // and other "Summon a Token to each adjacent location" patterns.
  // args: { tokenName, location: { scope?, distance? } }
  SUMMON_TOKEN_AT_LOCATIONS: 'summon_token_at_locations',
  // v1.77. Replacement-effect ops. Used inside WOULD_DIE /
  // WOULD_TAKE_DAMAGE entries. The runtime applies the listed ops in
  // order; PREVENT_* short-circuits the underlying event so the engine
  // skips it entirely.
  //
  // PREVENT_DEATH. Cancels the dying card's send-to-cemetery.
  // PREVENT_DAMAGE. Cancels the amount of damage (whole amount).
  //                  Optional args.amount caps reduction (e.g. "prevent
  //                  N damage"); omit for full prevention.
  // HEAL_FULL. Sets target.currentLife = baseLife. Target defaults to
  //             the WOULD_DIE event's dying card; can be overridden via
  //             args.target = 'self_bearer' etc.
  // BANISH_SELF. Sends the watcher card to the Banished pile (used
  //               by Gilded Aegis-style "use up the artifact").
  // RETURN_SELF_TO_HAND. Sends the watcher card back to its owner's
  //                       hand (Goswhit Helmet pattern).
  // REDIRECT_DAMAGE. Points the damage at the watcher itself
  //                   instead of the original target (Royal Bodyguard).
  //                   Optional args.cap = max amount to redirect.
  PREVENT_DEATH:       'prevent_death',
  PREVENT_DAMAGE:      'prevent_damage',
  HEAL_FULL:           'heal_full',
  BANISH_SELF:         'banish_self',
  RETURN_SELF_TO_HAND: 'return_self_to_hand',
  REDIRECT_DAMAGE:     'redirect_damage',
  // v1.77c. Return the TARGET of a would-die / would-take-damage
  // event back to its owner's hand. Lady Iseult pattern: "If another
  // nearby allied minion would die, return it to hand." Distinct
  // from RETURN_SELF_TO_HAND (which targets the watcher itself).
  RETURN_TARGET_TO_HAND: 'return_target_to_hand',
  // v1.77b. Generic "used this turn" marker. Pairs with the
  // `not_used_this_turn` condition so "first/once per turn" abilities
  // and replacement effects can self-gate. args.key is a per-card
  // string label so multiple per-turn abilities on the same card
  // don't collide. The flag clears at end-of-turn for the player
  // whose turn just ended (_stepPruneExpiredEffects).
  MARK_USED_THIS_TURN: 'mark_used_this_turn',
  // Untap the source card silently (Sir Lancelot's "The first time
  // Sir Lancelot fights each turn, untap him"). Pairs with a
  // not_used_this_turn condition + MARK_USED_THIS_TURN for the
  // once-per-turn gate.
  UNTAP_SELF: 'untap_self',
  UNTAP_IN_SCOPE: 'untap_in_scope',
  RETURN_FROM_CEMETERY_TO_HAND: 'return_from_cemetery_to_hand',
  // v1.77e. Transient self-buff fired from a replacement effect.
  // Bumps the watcher's currentAttack by args.delta and queues a
  // revert that fires in _stepPruneExpiredEffects at end of turn.
  // Askelon Phoenix's "gains +1 power this turn instead" pattern.
  GRANT_STAT_SELF_THIS_TURN: 'grant_stat_self_this_turn',
  // v1.77f. Sacrifice a minion atop the watcher (same cell) as the
  // replacement cost. Altar of Malachai's "sacrifice a minion atop
  // this Altar instead." Picks the first eligible minion matching
  // args.filter; if none exist, the entry no-ops and the underlying
  // event proceeds.
  SACRIFICE_HERE: 'sacrifice_here',
  // v1.77f. Once-per-game marker. Persists across turns (unlike
  // MARK_USED_THIS_TURN). Pairs with the not_used_this_game condition.
  // Used by Altar of Malachai's "Once per game" gate.
  MARK_USED_THIS_GAME: 'mark_used_this_game',
  // v1.77g. Deploy a transient WOULD_TAKE_DAMAGE replacement onto a
  // single chosen target. The installed entry redirects damage from
  // the target's nearby allies to the target itself, with a duration
  // expressed in turns of the casting player ("until your next turn"
  // → expires when caster's next turn starts). Enduring Faith pattern:
  // "Ward an allied minion. Until your next turn, it takes damage for
  // its nearby allies instead."
  INSTALL_DAMAGE_REDIRECT_NEARBY: 'install_damage_redirect_nearby',
  // v1.79. Forced movement. The runtime resolves the destination
  // independently from the picked-unit halo flow:
  //
  // TELEPORT_TARGET. Two-stage pick. Player first picks a unit
  //   matching `target` filter, then picks a destination cell from
  //   `destination` scope (forcedMovement.eligibleDestinations).
  //   Blink: { target: { ... ally minion ... },
  //            destination: { scope: 'nearby_of_unit' } }.
  //
  // PULL_TO_SOURCE. Single pick. Picked unit moves to the source's
  //   (or caster's) cell. No destination pick.
  //
  // PUSH_TARGET_AWAY. Single pick. Picked unit moves one cell away
  //   from the source along the cardinal axis it shares with the
  //   source. Ambiguous (diagonal) or off-grid pushes fizzle today;
  //   future: prompt for direction, route to void.
  TELEPORT_TARGET:   'teleport_target',
  PULL_TO_SOURCE:    'pull_to_source',
  PUSH_TARGET_AWAY:  'push_target_away',
  // Draco Corvus: "Tap → Teleport to target tapped minion to burrow
  // it." Single pick gated by args.target (tapped minion, global).
  // The SOURCE moves itself to the target's cell (self-move. The
  // mover isn't gated by `unmovable`; the picked target is, since the
  // burrow IS forced movement), then the target is force-buried via
  // the subsurface primitive. Codex lethality applies (no Burrowing
  // ⇒ dies underground). args: { target }
  SWOOP_AND_BURROW_TARGET: 'swoop_and_burrow_target',
  // v1.80. Prevention scaling. Reduces an incoming damage amount to
  // AT MOST args.amount (no-op if damage is already below the cap).
  // Distinct from PREVENT_DAMAGE which subtracts. Golden Harp pattern:
  // "Strike damage and life loss caused by nearby units is reduced to 2."
  CAP_DAMAGE: 'cap_damage',
  // v1.82. Single-target temporary power buff. Player picks a unit
  // matching `target`, then the unit gains args.delta power until the
  // end of the current turn (revert queue + cleanup mirror the
  // GRANT_STAT_SELF_THIS_TURN path Askelon Phoenix already uses).
  // Gigantism: "Give an allied unit +6 power this turn." Negative
  // deltas supported for "lose 3 power this turn" patterns.
  BUFF_TARGET: 'buff_target',
  // v1.96. Grant a free site placement to the source's controller
  // for this turn. Same mechanism as the Avatar's Tap → Play branch
  // (increments _siteGrants[controller], expires at end of turn).
  // Pathfinder pattern: pair with DRAW(atlas) so the player gets a
  // fresh site in hand AND the legal placement window to drop it.
  // v1.116. Optional `args.restriction: 'land' | 'water'` narrows the
  // grant to one site kind (Landmass "may play a land site" / Overflow
  // "may play a water site"). The placement gate enforces it via
  // `ops/siteRestriction.js`; absent restriction grants any site.
  GRANT_SITE_PLACEMENT: 'grant_site_placement',
  // The Apex of Babel: "then you may cast a spell for free." Grants the
  // controller a one-shot free-cast window for this turn (mirrors the
  // _siteGrants counter: a flag set on the host, glowed in hand, cleared
  // at turn end). While the window is open the controller may play one
  // spell from hand (Magic / Aura / Minion / carriable Artifact) paying
  // no mana. The cost-waiver flag is threaded into payCastingCost. The
  // window IS the rules-text "may"; declining it simply ends the turn.
  // args: (none)
  CAST_SPELL_FOR_FREE: 'cast_spell_for_free',
  // v1.103. Avatar of Water's "Flood a site adjacent to your body of
  // water until you do so again. You may teleport there." Specific to
  // this card; opens a cell pick constrained to sites adjacent to the
  // avatar's body-of-water component, unflood the previously-flooded
  // site, then prompt for optional teleport.
  AVATAR_OF_WATER_FLOOD: 'avatar_of_water_flood',
  // v1.103. Pathfinder's "If able, play the topmost site of your
  // atlas to an adjacent location and move there." Specific to this
  // avatar; draws the top of atlas into hand, gates the next site
  // placement to a cell cardinal-adjacent to the avatar, and auto-
  // moves the avatar to the placed cell on commit.
  PATHFINDER_TAP: 'pathfinder_tap',
  // v1.103. Interrogator's "Whenever an ally strikes an enemy Avatar,
  // draw a spell unless they pay 3 life." Fired by the on_strike
  // watcher trigger when watch_target_filter matches; pushes a prompt
  // to the OPPONENT (the struck Avatar's controller) asking whether to
  // pay 3 life. On decline, Interrogator's controller draws a spell.
  INTERROGATOR_DODGE: 'interrogator_dodge',
  // Bladedancer's "After her first attack each turn, Bladedancer may
  // take a step. When she does, she may attack a unit there." Fired by
  // the on_strike self-trigger gated by not_used_this_turn; opens a
  // cell-pick on her 1-step movement reach (tap state is irrelevant.
  // the ability grants the step), moves her there, then offers an
  // attack against a unit at the destination.
  BLADEDANCER_STEP: 'bladedancer_step',
  // v1.103. Apply a status keyword to the source card itself.
  // Realm-Eater's "becomes immobile until it digests." Fires
  // alongside CONVERT_WATCH_TARGET_TO_RUBBLE in the same trigger.
  // Silent-resolved in combat-trigger path. args: { keyword }.
  APPLY_STATUS_TO_SELF: 'apply_status_to_self',
  // v1.103. Geomancer's "Tap → Replace an adjacent Rubble with the
  // topmost site of your atlas." Opens a cell pick on cells cardinal-
  // adjacent to the avatar that contain a Rubble; on resolve, banishes
  // the Rubble and spawns the atlas-top site at that cell.
  GEOMANCER_REPLACE_RUBBLE: 'geomancer_replace_rubble',
  // v1.115. Avatar of Fire's "Tap → This turn, fire sites in your hand
  // are also Fireballs." Opens a hand-picker on the controller's Fire
  // Sites; the picked site moves to cemetery as a cast Magic spell and
  // Fireball's projectile resolves from the avatar's cell: 4 damage on
  // impact, 2 damage to each other unit at that location. One tap =
  // one Fireball; Avatars can only tap once per turn so the rules-text
  // "this turn" duration collapses to a single use.
  AVATAR_OF_FIRE_IGNITE: 'avatar_of_fire_ignite',
  // v1.111. Flamecaller's "Tap, Banish all your dead fire minions →
  // Shoot a projectile. It deals damage equal to the sum of their (F)."
  // Specific to this avatar; banishes every Fire Minion in the
  // controller's cemetery as the additional activation cost, then opens
  // a target pick that deals damage equal to the sum of those banished
  // cards' printed Fire thresholds. Tap cost is the standard schema
  // cost; the banish-all-dead-fire payment is part of the op resolution
  // (mirrors how witch_curse / pathfinder_tap fold extra payments into
  // the imperative resolution step).
  FLAMECALLER_FIRE_BLAST: 'flamecaller_fire_blast',
  // Sparkmage's "Tap → Target nearby location. Deal damage to another
  // random unit there equal to the sum of (A) on spells you've cast
  // this turn." Opens a cell-pick on nearby cells holding at least one
  // other unit; the resolver picks one at random and damages it for
  // the controller's per-turn air-threshold cast sum.
  SPARKMAGE_SHOCK: 'sparkmage_shock',
  // An allied unit takes up to `steps` optional steps (1-step movement reach
  // per pick), then strikes each enemy unit at `strike: 'destination'` (Leap
  // Attack) or at every cell it entered, `strike: 'path'` (Whirling Blades).
  // Pick the ally, then one cell prompt per step; a declined step ends the
  // walk. Strikes are plain power damage (no fight, no strike-back).
  // args: { steps, strike }.
  ALLY_STEP_STRIKE: 'ally_step_strike',
  // Grapple Shot: pick an ally, it shoots a projectile (cell pick over the
  // trails that reach a unit); the ally is dragged to the hit unit's cell and
  // may strike it there (optional modal). No hittable unit → fizzles.
  GRAPPLE_SHOT: 'grapple_shot',
  // Lilith: reveal the opponent's top spell to both seats. A minion is
  // summoned at the source's cell under the source's control; anything else
  // goes to the bottom of that spellbook. Silent.
  REVEAL_OPPONENT_TOP_SUMMON_HERE_OR_BOTTOM: 'reveal_opponent_top_summon_here_or_bottom',
  // Pith Imp: a server-random spell (non-site) leaves the opponent's hand for
  // the controller's, revealed to both, stamped `_stolenBy`; the per-commit
  // sweep hands it back once the source has left the realm. Silent.
  STEAL_RANDOM_SPELL_FROM_OPPONENT_HAND: 'steal_random_spell_from_opponent_hand',
  // Corruptor's "Your Beasts are Monsters, your Mortals are Undead,
  // and your Angels are Demons." Static, layer-2 (TYPE) subtype
  // replacement: every scoped card gets args.map merged into its
  // per-pass `_auraSubtypeMap`; `effectiveSubtypes` (cardFilter.js)
  // applies the swap at every rules-level subtype read. args:
  // { map: { beast: 'monster', ... }, filter }.
  TRANSFORM_SUBTYPES: 'transform_subtypes',
  // Layer-2 NAME alias (mirror of TRANSFORM_SUBTYPES for card names).
  // recomputeStaticAuras pushes args.name onto the scoped cards'
  // `_auraNameAliases`, rebuilt every pass so source-leave clears the
  // alias automatically. Every exact-name filter read (scope counts,
  // condition gates, cost mods) honors aliases via cardFilter.js.
  // Moonsong Talagelum: "Counts as a Grey Wolves." args: { name }
  ADD_NAME_ALIAS: 'add_name_alias',
  // Persecutor's "Once on your turn, Persecutor may step toward the
  // closest Evil, or brand an enemy so your cards treat them as Evil
  // this turn." The activated entry opens a 2-mode choice prompt;
  // 'step' runs a cell-pick on distance-reducing 1-step reach cells
  // (PERSECUTOR_STEP resolves the move), 'brand' opens a target pick
  // that grants the `branded` status (cleared at end of turn; the
  // Evil filter check honors it).
  PERSECUTOR_ACTION: 'persecutor_action',
  PERSECUTOR_STEP: 'persecutor_step',
  // Ranged keyword's Shoot activation ("Tap → Shoot a projectile that
  // stops after 1 step / X steps. Strike the hit unit."). Cell-pick op
  // so the shooter picks a DIRECTION on the trail overlays (same UX as
  // flamecaller_fire_blast); resolution declares the shot through the
  // CombatEngine (strike, no strike-back, no defend window).
  RANGED_SHOT: 'ranged_shot',

  // v1.103. Witch's "Curse target Avatar, choose one: spells cost (1)
  // more on their next turn, lose 2 life, or -3 power until your next
  // turn." Builds a 3-choice prompt that targets the opponent's Avatar
  // and dispatches the picked curse inline.
  WITCH_CURSE: 'witch_curse',
  // v1.103. Seer's "At the start of your turn, look at your topmost
  // site or spell. You may put it on the bottom of its deck." Pushes a
  // 2-choice prompt (spellbook vs atlas); the chosen pile opens in the
  // existing ScryDialog with 'opposite' destination allowed.
  SEER_SCRY_CHOICE: 'seer_scry_choice',
  // v1.103. Waveshaper's "Tap → Flood a site near your body of water
  // until you do so again. Tap minions without submerge there. They
  // don't untap the next time they would." Specific to this card; opens
  // a cell pick constrained to sites NEARBY (8-neighbor) the
  // Waveshaper's body of water, unfloods the previously-flooded site,
  // taps non-submerge minions at the picked cell, and marks them with
  // _skipNextUntap (honored by _stepUntapAll).
  WAVESHAPER_FLOOD: 'waveshaper_flood',
  // v1.98. Open the existing scry dialog on the controller's pile.
  // Used by on_turn_start triggers that want to peek at the top of a
  // deck (Seer's "look at your topmost spell at start of turn"). args:
  // { pile: 'spellbook' | 'atlas', count, allow_bottom: true }.
  // Resolved silently in _stepFireTriggers. No prompt drawer step,
  // the scry dialog IS the player interaction.
  OPEN_SCRY_TOP: 'open_scry_top',
  // Cast the controller's topmost spellbook card to the source's cell
  // (or the bearer's cell when the source is an equipped Relic),
  // gated on args.filter matching the top card. Mana cost = printed
  // cost + args.cost_delta (floor 0); thresholds gate normally. The
  // activation gate (_canActivateEntry) hides the menu row whenever
  // the top card doesn't match or can't be afforded, so activating
  // never leaks hidden information. Cradle of Etherrum: "You may cast
  // Dragons from the top of your spellbook here for (1) less."
  // args: { filter, cost_delta }
  CAST_TOP_OF_SPELLBOOK: 'cast_top_of_spellbook',
  // Xeraphine Konrul: "At the end of your turn, you may choose three
  // of your dead minions. Banish two to return the remaining one to
  // hand." Two-stage cemetery pick: stage 1 selects args.choose
  // minions, stage 2 picks which of them returns to hand; the rest
  // banish. Cancelling either stage (close / ESC) aborts with no
  // state change. Nothing mutates until the stage-2 pick lands.
  // args: { choose: 3, return: 1 }
  CEMETERY_RITES: 'cemetery_rites',
  // Kairos the Archivist, Genesis: snapshot every in-play card's
  // cell / tap / damage / subsurface / side onto the source
  // (`_realmArchive`, ops/kairosArchive.snapshotRealm) and broadcast
  // it so the peer's instance carries the same archive. Deathrite
  // may fire on either client. Silent. args: (none)
  ARCHIVE_REALM_STATE: 'archive_realm_state',
  // Kairos the Archivist, Deathrite: diff the archive against the
  // live board (ops/kairosArchive.buildRestorePlan) and execute each
  // step through host primitives. Forced move, _applyTap, life
  // write, _setSubsurface, pile-return spawn. Cards that entered
  // play after the archive are untouched; archived cards that left
  // play return at their recorded cell. Then Kairos banishes itself
  // from the cemetery. args: (none)
  RESTORE_ARCHIVED_REALM: 'restore_archived_realm',
  // v1.97. Convert the watch-target Site into a Rubble token in
  // place. Realm-Eater pattern: "Destroys sites it successfully
  // attacks." Resolved silently in combat trigger firing. No-op for
  // non-Site watch targets (caller should gate via watch_target_filter).
  CONVERT_WATCH_TARGET_TO_RUBBLE: 'convert_watch_target_to_rubble',
  // v1.95. Damage the card carried in the watch context. Realm /
  // combat triggers carry a watchTarget (the entering / dying / struck
  // card); this op damages that card by args.amount. Druid pattern:
  // ON_REALM_ENTRY filtered by watch_target_filter(enemy + near_source)
  // → DAMAGE_WATCH_TARGET(1). Resolved silently by _fireRealmTrigger.
  DAMAGE_WATCH_TARGET: 'damage_watch_target',
  // v1.90. Heal a specific player by amount. Distinct from GAIN_LIFE
  // (caster's own healing). Combat triggers carry `controller` so this
  // op heals the source's controller. Pairs with ON_DAMAGE_DEALT for
  // vampire-style "strike damage heals you" effects. Honors codex
  // Healing: blocked at Death's Door (caller checks via isAtDeathsDoor).
  HEAL_PLAYER: 'heal_player',
  // v1.89. Flat cost modifier for spells cast by the target player.
  // Lives on the EffectLedger so it shares the existing expiry +
  // sync + recompute lifecycle. Positive delta = costs more, negative
  // = costs less. Tax Collector pattern: "On your opponent's next
  // turn, their spells cost (1) more." → MODIFY_CAST_COST with
  // delta: 1, target_player: 'opponent', duration: this_turn (resolved
  // against the OPPONENT's turn number).
  MODIFY_CAST_COST: 'modify_cast_cost',
  // v1.86. Search a deck for a card matching `filter` and put it into
  // the player's hand. Per codex Search: "If you search a public zone,
  // you must find what you're searching for, if possible. If you
  // search a private zone, you may choose to fail to find it." Reuses
  // the existing PileSearchDialog UI; only cards matching the filter
  // are tile-clickable. After the player picks (or cancels), the
  // searched pile is shuffled.
  // args: {
  //   pile: 'spellbook' | 'atlas' | 'cemetery',
  //   filter: { type?, subtype?, cost_at_most?, rarity? },
  //   shuffle_after: true,        // codex default
  // }
  // Highland Princess: search spellbook for an Artifact, cost ≤ 1.
  // Call to War: search spellbook for an Exceptional Mortal.
  // Common Sense: search spellbook for an Ordinary card.
  SEARCH_DECK: 'search_deck',
  // Site-granted triggered ability primitive. The source (e.g. Druid
  // back face) emits a STATIC entry whose effect is "install this
  // triggered entry on every card matching the source entry's scope."
  // The trigger entry rides verbatim in args.trigger_entry. Same
  // shape as any automation entry. Receivers store the granted entries
  // in `card._grantedTriggers[]`; the array is rebuilt fresh on every
  // `recomputeStaticAuras` pass so source-leave automatically clears
  // the grant. The runtime walks `_grantedTriggers` alongside intrinsic
  // automation when checking which entries fire for a given event.
  //
  // args: { trigger_entry: { trigger, condition?, effect, duration, scope } }
  // or { copy_trigger, as_trigger }: copy each receiver's printed entries
  // under another trigger (The Geistwood: Genesis also becomes Deathrite).
  // outer scope on the granting entry: who receives the grant.
  GRANT_TRIGGER_TO_SCOPE: 'grant_trigger_to_scope',
  // Enchantress's "animate target aura until your next turn. It's an
  // aura minion with power equal to its cost." Opens a target-pick on
  // cards in play with the 'Aura' subtype; on confirm, mutates the
  // picked card's type → 'Minion' and sets baseAttack/baseLife =
  // printed cost (same recipe as Animist's Magic→Spirit transform).
  // Identity snapshot stored on the card; reverted at the start of
  // the caster's next turn via the delayed-identity-revert queue.
  //
  // args: { duration: 'until_caster_next_turn' }. Only one timing
  // shape supported today; field reserved for future variants.
  ANIMATE_TARGET_AURA: 'animate_target_aura',
  // Send the source card itself to its controller's cemetery. Sorcery's
  // standard "dispel an aura" wording. The aura leaves play. Resolved
  // silently inside the turn-trigger pipeline. Used by every aura whose
  // text ends with "dispel <self>" or "destroy <self>" (Hamlet's /
  // Castle's Ablaze, Year of the Blaze, Atlantean Fate, …).
  DISPEL_SELF: 'dispel_self',
  // The source's controller discards a spell from hand (Wizard's Den:
  // "Discard a spell when this site is first attacked successfully").
  // `args.once` stamps a one-time flag so it fires only on the first hit.
  DISCARD_SPELL: 'discard_spell',
  // Force the watch-target unit into a subsurface region. Mariner's
  // Curse submerges (region 'underwater'), Rest in Peace burrows
  // (region 'underground'). Fired from an on_enter_location watcher on
  // an aura; the entering unit rides opts.watchTarget, never a
  // player-picked target. Silent. No prompt.
  // args: { region: 'underwater' | 'underground' }
  FORCE_REGION_WATCH_TARGET: 'force_region_watch_target',
  // Stamp a "lasts N of your turns" countdown on the source card. Fired
  // from an on_play entry; the engine's auraDuration module owns the
  // counter mechanics. At the end of each of the controller's turns
  // the counter decrements; the card dispels when it reaches 0.
  // args: { turns: number ≥ 1 }
  BIND_TURN_LIFESPAN: 'bind_turn_lifespan',
  // Continuous keyword SUPPRESSION (mirror of GRANT_KEYWORD). Pushes
  // the keyword onto every scope-matched card's _suppressedKeywords
  // list so printed + aura-granted instances of the keyword are denied.
  // Resolved by recomputeStaticAuras. Entangle Terrain pattern:
  // "Minions occupying affected sites lose Airborne and are Immobile"
  // → REVOKE_KEYWORD(airborne) + GRANT_KEYWORD(immobile).
  // args: { keyword: 'airborne', filter: { type: 'Minion' } }
  REVOKE_KEYWORD: 'revoke_keyword',
  // Pick one random card in the entry's scope matching `each` filter
  // and damage it. Thunderstorm pattern: "deal 3 damage to a random
  // unit atop affected sites." Random pick uses the engine's RNG so
  // it's deterministic under a seeded session for replay/test parity.
  // args: { amount, each: { type?, subtype?, scope?, exclude_self? } }
  DAMAGE_RANDOM_IN_SCOPE: 'damage_random_in_scope',
  // Summon a named token to every covered cell that has no current
  // unit, with an optional controller filter on the underlying site
  // (Invasion: "to each affected enemy site with no units"). Extends
  // SUMMON_TOKEN_AT_LOCATIONS for the aura case where the "location"
  // is the aura's own covered set rather than a scope-relative
  // expansion (adjacent/nearby).
  // args: { tokenName, site_controller?: 'enemy'|'self', require_empty?: true }
  SUMMON_TOKEN_AT_COVERED: 'summon_token_at_covered',
  // Marker static entry on a wall (INTERSECTION_2 aura). The movement-
  // reach engine reads BLOCK_EDGE_TRAVERSAL entries from walls between
  // a unit's source and destination cells and drops blocked
  // destinations from the reach set. The `if` predicate decides which
  // units the wall blocks (Wall of Air blocks Airborne + power≤2;
  // Wall of Ice blocks ground traversal). The op never "fires" via
  // dispatchSilentOp. It's purely declarative state read by
  // wallTraversal.js.
  // args: { if: <unit-predicate> }
  BLOCK_EDGE_TRAVERSAL: 'block_edge_traversal',
  // Declarative summon-legality extension: while this aura is in
  // play, its covered cells become legal spawn destinations for hand
  // cards matching `args.filter`. Read by `auraExtendedSpawnCells`
  // and unioned into spawnableCellsForMinion's extraCells parameter.
  // Used by Crusade ("may summon earth minions to affected sites"),
  // Jihad (fire), Eclipse (Evil), Summoning Sphere ("may summon
  // minions"), Evil Presence (Spirits).
  // args: { filter: { element?, subtype?, type? } }
  EXTEND_SPAWN_LEGAL_AT_COVERED: 'extend_spawn_legal_at_covered',
  // Grant a keyword to the unit carried in opts.watchTarget. Mirror
  // of DAMAGE_WATCH_TARGET / FORCE_REGION_WATCH_TARGET. Operates on the
  // entering / dying / struck card without a player pick. Evil
  // Presence: "When you summon a Spirit here, give it Charge."
  // When keyword === 'charge' the host also clears summoning sickness
  // so the unit can act on the turn it was summoned.
  // args: { keyword }
  GRANT_KEYWORD_TO_WATCH_TARGET: 'grant_keyword_to_watch_target',
  // Pick the min/max-power minion in scope and kill it. If multiple
  // tie at the extremum, deterministic pick (first encountered) for
  // v1. Codex says "choose one" but a player-pick prompt is friction
  // for the silent on_turn_end path. When `dispel_if_none` is true
  // and the scope is empty (no eligible minions), dispel the source.
  // Black Plague / Great Famine pattern.
  // args: { direction: 'min'|'max', filter?, dispel_if_none?: boolean }
  KILL_BY_POWER_IN_SCOPE: 'kill_by_power_in_scope',
  // Static aura that adds 1 mana per covered site (allied OR enemy)
  // to that site's controller. Resolved each recomputeStaticAuras
  // pass. The engine splits the count by side and pushes two
  // conditional-grant ledger entries (one per controller) so each
  // player's `_stepProvideMana` picks up their own bonus. Abundance
  // pattern: foreign sites covered by Abundance still produce the
  // extra mana, just to the enemy's pool rather than yours.
  // args: (none today; future expansion: filter on site rarity etc.)
  GRANT_MANA_PER_COVERED_SITE: 'grant_mana_per_covered_site',
  // Declarative static entry on an aura. Read by the site placement
  // engine, which removes the aura's covered cells from the legal-
  // spawn set for ANY incoming Site card. Salt the Earth pattern.
  // (op never "fires." Purely state inspected by the placement
  // pipeline, same as BLOCK_EDGE_TRAVERSAL.)
  BAN_SITE_PLACEMENT_AT_COVERED: 'ban_site_placement_at_covered',
  // Static aura that zeros out a site's contribution of a named
  // element threshold (Drought: "provides no water threshold"). Writes
  // to a per-card `_thresholdSuppression` field consumed by
  // regions.elementsAtCell. Reset every recompute pass alongside the
  // aura-status / aura-suppressed-keyword lists.
  // args: { element: 'air'|'earth'|'fire'|'water', filter?: <card filter> }
  SUPPRESS_THRESHOLD_ON_SCOPE: 'suppress_threshold_on_scope',
  // Declarative static marker (BLOCK_EDGE_TRAVERSAL pattern. Never
  // fires through dispatchSilentOp): while the source is in play,
  // cards matching args.filter cast TO the source's cell skip the
  // threshold gate. Read by ops/thresholdWaiver.js from the
  // affordability + placement pipelines. Dragonlord's Lair: "Dragons
  // cast to this site require no threshold." args: { filter }
  WAIVE_THRESHOLD: 'waive_threshold',
  // Iterate cards in scope matching `filter` and force-submerge each
  // through the host's subsurface primitive. Units lacking the
  // Submerge keyword die per codex (the host's _setSubsurface enforces
  // this). The Great Drowning of Men / Atlantean Fate Genesis pattern.
  // args: { filter?: <card filter> }. Defaults to Minion or Artifact
  SUBMERGE_EACH_IN_SCOPE: 'submerge_each_in_scope',
  // Iterate every in-play card matching args.filter and move it to
  // the source's cell (units stack per Sorcery location rules).
  // Skips `unmovable` cards. For equipped Monuments the attach
  // pipeline anchors on the HOST site instead of the artifact. Shrine
  // of the Dragonlord: "Genesis → Teleport all Dragons here."
  // args: { filter }
  TELEPORT_EACH_TO_SOURCE: 'teleport_each_to_source',
  // Buff every card in scope matching `target_filter` by
  // (delta × count) where count is the number of OTHER cards in scope
  // matching `count.filter`. A Midsummer Night's Dream pattern: Faeries
  // gain +1 power per Mortal in the covered cells. Generalises
  // GRANT_STAT_PER_EACH_SELF (which buffs the source only) to buff
  // a different subset based on a counted subset within the same scope.
  // args: { delta, target_filter, count: { filter, exclude_self? } }
  GRANT_STAT_PER_OTHER_IN_SCOPE: 'grant_stat_per_other_in_scope',
  // Static self-dispel: the source aura sends itself to its
  // controller's cemetery when the source's scope contains zero
  // cards matching `args.filter`. Evaluated every recomputeStaticAuras
  // pass, so the check fires on-play, on board changes, and after
  // turn passes. A Midsummer Night's Dream: "Dispel if there are no
  // Faeries here."
  // args: { filter: <card filter> }
  DISPEL_SELF_IF_SCOPE_EMPTY: 'dispel_self_if_scope_empty',
  // Declarative marker: artifacts equipped to a host in the aura's
  // covered set cannot be detached. UI reads the engine helper
  // `lockedEquipmentIds` to disable the detach button + show a
  // tooltip explaining the lock source. Purely state inspected by
  // the UI. Never fires through dispatchSilentOp. Cursed Iron.
  BAN_ARTIFACT_DETACH_AT_COVERED: 'ban_artifact_detach_at_covered',
  // For each artifact equipped to a host in the aura's covered set,
  // damage the host by `amount`. Cursed Iron: "each deals 1 damage to
  // its bearer at the end of each turn." Multiple artifacts on the
  // same host stack damage. Routes through the host adapter's
  // damageTarget so Ward / replacement effects honour normally.
  // args: { amount }
  DAMAGE_BEARER_PER_ARTIFACT_IN_SCOPE: 'damage_bearer_per_artifact_in_scope',
  // For every host in the source's covered set, send each equipped
  // artifact to its owner's cemetery. Year of the Blaze pattern:
  // "destroy affected sites and EVERYTHING here." Without this op
  // a subsequent `kill_each` would let the host's artifacts detach
  // to the field and survive, since the each-iterator snapshots its
  // match list BEFORE the first kill. Runs first so no detach occurs.
  // args: (none)
  DESTROY_EQUIPPED_IN_SCOPE: 'destroy_equipped_in_scope',
  // Move the source aura to a cardinal-adjacent site cell it hasn't
  // visited before. Wildfire pattern: "move Wildfire to an adjacent
  // location it hasn't visited. If none remain, dispel Wildfire."
  // Initialises `_visitedCells` lazily with the source's current cell
  // so the very first move doesn't try to step back where it started.
  // On no-unvisited-adjacent-site, the source dispels.
  MOVE_AURA_TO_UNVISITED_ADJACENT: 'move_aura_to_unvisited_adjacent',
  // Optional one-step self-move for intersection-anchored auras.
  // Thunderstorm pattern: "then you may move Thunderstorm one step."
  // Surfaces a Confirm prompt (Cancel declines the "may"), then a
  // cell-pick on the strips of newly covered cells per cardinal
  // direction. The four strips are disjoint, so the picked cell maps
  // back to exactly one destination anchor.
  MOVE_AURA_ONE_STEP: 'move_aura_one_step',
  // On-play op for Sow the Earth: pull the controller's top atlas
  // site and spawn it at the aura's covered cell. No-ops when the
  // atlas is empty or its top card isn't a Site (matches Sorcery's
  // "if able" implicit semantics).
  PLAY_TOP_ATLAS_AT_COVERED: 'play_top_atlas_at_covered',
  // Static aura that multiplies the printed thresholds of every site
  // in scope by `factor` (Sow the Earth: factor=2). Writes to a
  // per-site `_thresholdMultiplier` field consulted by
  // regions.elementsAtCell. Mirrors SUPPRESS_THRESHOLD_ON_SCOPE
  // structurally; both reset every recompute pass.
  // args: { factor }
  MULTIPLY_THRESHOLDS_ON_SCOPE: 'multiply_thresholds_on_scope',
  // For each card in the entry's scope matching `filter` (typically
  // { type: 'Site' }), convert it in place to a Rubble tile. Same
  // primitive as CONVERT_WATCH_TARGET_TO_RUBBLE but iterates `scope`
  // instead of taking a watchTarget. Skip cards that are already
  // Rubble (the cardId === 'sorcery-rubble' check). Resolved silently.
  CONVERT_EACH_IN_SCOPE_TO_RUBBLE: 'convert_each_in_scope_to_rubble',
  // Skip the affected player's NEXT turn. Stamps `_skipNextTurn` on
  // that player's avatar (synced via the dedicated card:skipNextTurn
  // broadcast); the turn runner consumes it at the start of their next
  // turn, which then immediately passes. Vaults of Zul: "they draw
  // three cards and skip their next turn." The affected player is the
  // entry's resolved controller (opts.controller in the silent path).
  // args: (none)
  SKIP_NEXT_TURN: 'skip_next_turn',
  // Record that a first-time-at-cell entry has now fired for the
  // source's current cell. Pairs with the `first_visit_to_cell`
  // condition (read-only gate); this op does the write so an optional
  // "may" the player declines doesn't burn the first-time. `per_cell`
  // mirrors the condition: false stamps a single boolean (Vaults of
  // Zul, one cell), true appends the source's cell key to a list
  // (Wayfaring Pilgrim, one shot per distinct corner).
  // args: { per_cell?: boolean }
  MARK_CELL_VISITED: 'mark_cell_visited',
  // Tap the drawing player's strongest untapped minion (effective
  // power, first-encountered tie-break). Chains of Prometheus:
  // "Whenever a player draws a card, that player taps their strongest
  // untapped minion." Resolved inline by the on_draw firing path
  // through the unified tap primitive (host._applyTap); no args today.
  TAP_STRONGEST_MINION:   'tap_strongest_minion',
  // Controller cell-pick to break a TAP_STRONGEST_MINION power tie.
  // When two or more of the drawing player's untapped minions share the
  // highest effective power, the rules let that player CHOOSE which one
  // taps (not random / first-encountered). The on_draw path opens a
  // cell-pick over the tied minions' cells; executeCellOp taps the tied
  // minion at the picked cell via the unified tap primitive. args carry
  // the tied minion ids ({ tiedIds }) so the resolver scopes the pick to
  // the actual tied set.
  TAP_PICKED_TIED_MINION: 'tap_picked_tied_minion',
  // Increment a serializable level/charge counter on the source card.
  // Stored as a plain integer `_counters` on the card instance (survives
  // JSON sync natively, no Set rehydration). The Immortal Throne: "they
  // draw a card and add a level counter." Resolved inline from the
  // on_spell_cast firing path. args: { amount?: 1 }
  ADD_COUNTER:            'add_counter',
  // Mass status grant. Apply a keyword to EVERY in-play card matching
  // `filter` within `scope.where` (default global). Mirror of
  // GRANT_STATUS_TO_TARGET for the no-pick, scope-resolved case, the
  // status analogue of KILL_EACH / DAMAGE_EACH. Routes each card
  // through the unified toggleCardStatus primitive (apply + badge
  // rebuild + broadcast + recompute), so opponents and Faith-Incarnate
  // style per-status auras stay in sync. Vanishment: "Give all allied
  // minions Stealth." args: { keyword, filter, exclude_self? }
  GRANT_STATUS_EACH:      'grant_status_each',
  // Cemetery multi-pick (any number) → shuffle each picked card back
  // into the deck it belongs to (Atlas for Sites, Spellbook otherwise),
  // then shuffle those decks. Opens the existing pile-view dialog in
  // multi-select mode; nothing mutates until Accept. Pendragon Legacy:
  // "Shuffle any number of Unique cards from your cemetery into your
  // decks." args: { filter? }. Gates which dead cards are eligible
  // (Pendragon: { rarity: 'Unique' }).
  SHUFFLE_FROM_CEMETERY_INTO_DECK: 'shuffle_from_cemetery_into_deck',
  // Cemetery multi-pick (up to `max`) → return each picked card to the
  // BOTTOM of the deck it belongs to (Atlas for Sites, Spellbook
  // otherwise). No shuffle. Codex "bottom of their decks." Reuses the
  // pile-view multi-select; nothing mutates until Accept. Return to
  // Nature: "Return up to three cards from a cemetery to the bottom of
  // their decks." args: { max?: 3, filter? }.
  RETURN_FROM_CEMETERY_TO_DECK_BOTTOM: 'return_from_cemetery_to_deck_bottom',
  // Windowed top-N spellbook pick → draw up to `pick` distinct-named
  // cards matching `filter`, then put the REST of the window at the
  // BOTTOM of the spellbook (preserving order). Opens the existing
  // pile-view dialog scoped to ONLY the top `window` cards (never the
  // whole library); nothing mutates until Accept. Black Mass: "Search
  // your top seven spells. You may reveal and draw three different Evil
  // minions from among them. Put the rest at the bottom of your
  // spellbook." args: { window: 7, pick: 3, filter: { type, subtype },
  // distinct_by: 'name' }.
  SEARCH_TOP_DRAW_REST_TO_BOTTOM: 'search_top_draw_rest_to_bottom',
  // Target op (Gift of the Raven): install a one-shot lifelink grant on
  // the picked allied minion. Pushes a transient on_damage_dealt entry
  // (`buildStrikeLifelinkEntry`) onto the target's `_transientAutomation`
  // The next strike that deals damage to a unit this turn heals the
  // minion's controller by the damage dealt, then the grant is consumed.
  // The combat-trigger path walks transient entries on the striker, so
  // no static-aura recompute is involved. args: (none; reads target).
  INSTALL_ON_STRIKE_LIFELINK: 'install_on_strike_lifelink',
  // Target op (Gift of the Frog): summon a named catalog token onto the
  // picked allied minion's cell (units stack). Resolves the token row
  // via findCardCatalogToken and drops it at the target's cell centre
  // through the spawnTokenAtPoint primitive. No click-to-place, the
  // destination is fixed by the rule. args: { tokenName }.
  SUMMON_TOKEN_AT_TARGET: 'summon_token_at_target',
  // Post-pick follow-up (Fade): draw a card only when the picked minion
  // currently occupies an enemy-controlled site. Rides the target-pick
  // followup chain so it resolves against the same chosen minion.
  // args: { amount?: 1, source?: 'spell' }.
  DRAW_IF_TARGET_ON_ENEMY_SITE: 'draw_if_target_on_enemy_site',
  // Tap a single picked target via the unified tap primitive
  // (host._applyTap). Pairs with grant_status_to_target in the same
  // entry so one pick silences + taps the chosen minion. Grievous
  // Insult: "Silence target nearby minion this turn and tap it. Draw a
  // spell." Target pick reuses the standard halo flow; the tap routes
  // through _applyTap so summoning-sickness / disabled gating + sync +
  // skip-untap bookkeeping stay single-sourced. args: { target }
  TAP_TARGET:             'tap_target',
  // Pick a target Site, flood it (grant the `flooded` status), then
  // grant a keyword to every minion standing on that site. Geyser:
  // "This turn, flood target site and give each minion there Airborne.
  // Draw a card." The flooded + keyword grants carry the entry's
  // duration; the this-turn revert rides the shared status-expiry
  // pipeline. args: { keyword }. The per-minion status applied at the
  // picked site (Airborne).
  FLOOD_TARGET_SITE:      'flood_target_site',
  // Banish EVERY card matching `each` filter in the entry's scope (no
  // pick), mirror of KILL_EACH / DAMAGE_EACH for the banish primitive.
  // Sjaelström: "Banish all nearby Spirits and auras." Pairs with a
  // trailing draw whose `count` reads the just-banished set so "draw a
  // spell for each" resolves to the live removal count. args: { each }
  BANISH_EACH:            'banish_each',
  // Draw for the OPPONENT of the watch-target card's controller. Fired
  // from the on_tap watcher path: the tapped minion's opponent draws a
  // spell (False Idol). Reads opts.watchTarget to find the tapped
  // minion's controller, then routes host.drawCard for the other
  // player. args: { amount?: 1, source?: 'spell' | 'site' }.
  DRAW_FOR_WATCH_TARGET_OPPONENT: 'draw_for_watch_target_opponent',
  // Damage a target minion standing above or below a water site that is
  // cardinally adjacent to the CASTER, then pull it to the caster's
  // location. Fire Harpoons!: "Deal 1 damage to target minion above or
  // below an adjacent Water site and pull it to the caster's location.
  // Draw a card." The runtime precomputes the legal target cells (water
  // sites cardinally adjacent to the caster) and injects them as
  // `filter.cells` so the halo only lights legal minions; the picked
  // minion is then pulled to the caster (pull_to_source recipe).
  // args: { amount }. Defaults to 1.
  DAMAGE_SITE_RELATIVE_TARGET: 'damage_site_relative_target',
  // Pick a target water Site, then pull an aboveground unit cardinally
  // adjacent to it INTO the site's cell. Riptide: "Target water site
  // pulls in an aboveground unit it's adjacent to. Draw a card." Two
  // stages: site pick (filter { type: 'Site', element: 'water' }), then
  // a unit-pick halo limited to surface units on adjacent cells; the
  // chosen unit moves to the site cell via the forced-move primitive.
  PULL_TO_TARGET_SITE:    'pull_to_target_site',
  // v1.116. Feign Death's "Trigger an allied minion's Deathrite."
  // Lead target-pick op: the player picks an allied minion, then its
  // on_death (Deathrite) automation fires in-band WITHOUT the minion
  // leaving play (same applyCardAutomation dispatch fireDeathriteOnLeave
  // uses). Pairs with a GRANT_STATUS_TO_TARGET followup (Stealth) on the
  // same pick and a trailing self-controller DRAW. args: { target }
  TRIGGER_TARGET_DEATHRITE: 'trigger_target_deathrite',
  // v1.116. Garden of Eden's "Players can't draw more than one spell
  // each turn." Static presence marker only: the Spellbook draw choke
  // point (cardPileLifecycle.drawCard) scans for any in-play card whose
  // automation carries this op and, if present, blocks a player's 2nd+
  // spell draw this turn. Per-turn spell-draw counts reset at turn
  // start. Enforced via `ops/spellDrawCap.js`. args: {}
  LIMIT_SPELL_DRAWS:      'limit_spell_draws',
  // Sacrifice-cost draws. The entry's `cost` carries a `sacrifice` entry
  // whose payment is deferred (mirror of the `discard` cost flow): the
  // player picks a minion to send to its controller's Cemetery via the
  // synced sendCardToPile primitive, THEN the draw resolves. Two cost
  // shapes:
  //   { type: 'sacrifice', target: <filter>, exclude_self? }. Pick a
  //     minion on the board (Cauldron Crones: "another minion here").
  //     The pick reuses the standard target-pick halo flow; the source's
  //     cell anchors the `here` scope.
  //   { type: 'sacrifice', self: true }. Sacrifice the source itself,
  //     no pick (Wiccan Tools). Resolves inline.
  // The op carries the trailing draw: { amount, source }. Cauldron Crones
  // is optional ("may"). The Genesis Confirm prompt IS the may; Cancel
  // declines for free. Activated entries (Wiccan Tools) are deliberate so
  // there is no extra confirm.
  SACRIFICE_DRAW:         'sacrifice_draw',         // args: { draw: { amount, source }, sacrifice: { target?, self?, exclude_self? } }
  // Pact with the Devil. Choice cost: sacrifice the casting Spellcaster
  // OR lose ceil(life/2), THEN draw. The cast pipeline supplies the
  // caster id via casterOverrides.casterId; "sacrifice the caster" routes
  // through sendCardToPile, the life option through loseLife. Both
  // resolve through a 2-option Confirm prompt. args: { draw: { amount } }
  PACT_SACRIFICE_DRAW:    'pact_sacrifice_draw',    // args: { draw: { amount } }
  // Sphinx Sarcophagus. Genesis cost: banish two identically-named
  // spells from either Cemetery, THEN draw a spell. A deferred two-pick
  // (mirror of the discard flow): the player opens a Cemetery and picks
  // one spell, then a second spell sharing the same printed name; both
  // banish via the synced pile primitive before the draw resolves.
  // args: { draw: { amount, source } }
  BANISH_PAIR_DRAW:       'banish_pair_draw',       // args: { draw: { amount, source } }
  // Riddle Sphinx middle clause: "an opponent may exchange your top and
  // bottommost spells." Targets the controller's spellbook. The decision
  // belongs to the OPPONENT (non-controller), but the mutation lands on
  // the controller's OWN spellbook. Which in multiplayer only the
  // controller's client holds in full. The controller emits a
  // `requestPileEndSwap` to the opponent; that client surfaces a yes/no
  // Confirm and replies `pileEndSwapResult`; on a yes the controller
  // swaps the pile's ends and broadcasts pile:sync so both clients agree.
  // The decision is never made independently per client. Resolved via
  // `ops/opponentMaySwapPileEnds.js`. args: { pile: 'spellbook' }
  OPPONENT_MAY_SWAP_PILE_ENDS: 'opponent_may_swap_pile_ends',
  // Pnakotic Manuscript bearer ability: "Tap → Reveal your topmost
  // spell and draw it. Bearer takes damage equal to that card's cost."
  // Activated entry living on the artifact; the resolver reads the
  // bearer's spellbook top, logs the reveal, draws it through the
  // unified draw funnel, then deals that card's printed mana cost to
  // the bearer (the equipped host) via _applyDamageToTarget. The
  // bearer is the equipped host, found by walking equippedCards.
  // args: (none)
  REVEAL_DRAW_DAMAGE_BEARER: 'reveal_draw_damage_bearer',
  // Seelie Court: "At the start of your turn, players draw a spell for
  // each Faerie in the realm." on_turn_start trigger; BOTH players draw
  // a count equal to the number of in-play cards matching args.filter
  // (subtype Faerie, any controller). The existing dynamic-count `draw`
  // op only draws for the source's controller; this op fans the same
  // count to every seat. args: { filter, source?: 'spell' }
  ALL_PLAYERS_DRAW_PER_SCOPE: 'all_players_draw_per_scope',
  // Salmon of Knowledge granted ability: "Other units at this site have
  // 'Tap → Roll a d20. On a 14+, banish Salmon of Knowledge and draw
  // three spells.'" Installed onto units sharing Salmon's cell via
  // grant_trigger_to_scope; the activating unit's controller rolls a
  // d20 (locally, the outcome syncs through the banish + draw board
  // mutations), and on a 14+ banishes the granting source (found via
  // the entry's _sourceCardId stamp) and draws args.draw spells.
  // args: { threshold: 14, draw: 3, die: 20 }
  ROLL_BANISH_SOURCE_DRAW: 'roll_banish_source_draw',
  // Erik's Curiosa: "Rip Erik's Curiosa to pieces → Draw a card from
  // your collection." Opens the controller's in-match Collection pile
  // in the existing pile-search dialog; the picked card goes to hand
  // (codex "draw from your collection" = take any card from it). The
  // "rip to pieces" cost banishes the artifact itself. Paid up front
  // by the activation's banish_self cost handling. args: (none)
  DRAW_FROM_COLLECTION: 'draw_from_collection',
  // 13 Treasures of Britain on_pickup reward: "bearer's controller
  // draws a card." Silent (no Confirm). The draw is automatic per the
  // card text. The firing controller (the bearer's controller, threaded
  // from the equip choke point) draws args.amount spells through the
  // unified host.drawCard funnel. args: { amount, source?: 'spell' | 'site' }
  DRAW_FOR_BEARER:        'draw_for_bearer',
  // Buried Treasure / Sunken Treasure on_surface reward: "its
  // controller sacrifices it and draws two cards." Silent. Automatic
  // per the card text. The controller draws args.draw.amount spells,
  // then the source sacrifices itself (sendCardToPile → Cemetery, the
  // synced pile primitive). Draw resolves BEFORE the sacrifice so the
  // source's controller is read off the still-in-play card.
  // args: { draw: { amount, source?: 'spell' | 'site' } }
  SACRIFICE_SELF_DRAW:    'sacrifice_self_draw',
  // Buried Treasure / Sunken Treasure cast clause: "If cast, conjure
  // this UNDER an allied land/water site of an opponent's choice." The
  // op opens an OPPONENT cell-pick over the controller's own land
  // (Buried) / water (Sunken) sites; the chosen cell conjures the
  // Treasure there stamped subsurface (underground for land, underwater
  // for water) so it sits buried until carried up. At which point the
  // on_surface clause becomes reachable. The opponent drives the pick,
  // so the source's controller never chooses where their own Treasure
  // is buried. args: { element: 'land' | 'water' }
  CONJURE_UNDER_SITE:     'conjure_under_site',
  // 13 Treasures of Britain on_pickup follow-up: "Teleport the Treasures
  // to the top of a RANDOM site or void." After the bearer's draw, the
  // Treasures detach from the bearer and re-spawn atop a uniformly
  // random board cell (every cell is a site or a void). The firing
  // client rolls the cell ONCE (the trigger fires on a single client)
  // and the synced place primitive broadcasts the chosen position, so
  // the opponent never rolls independently. args: (none)
  TELEPORT_TO_RANDOM_SITE_OR_VOID_TOP: 'teleport_to_random_site_or_void_top',
  // Searing Truth: "Target player draws and reveals two spells, then
  // takes damage equal to the higher mana cost." Cross-client forced
  // reveal-draw. The op resolves on the CASTER's client by emitting a
  // `forceRevealDraw` request to the target seat; that player's client
  // draws + reveals from its OWN hand (the only client that can) and
  // replies `revealDrawResult` with the higher cost, which the caster
  // applies to the target's Avatar via the damage funnel. The draw
  // CANNOT run on the caster's client because addToHand always routes
  // to the local hand in multiplayer. args: { count?: number }. 2.
  FORCE_REVEAL_DRAW_DAMAGE: 'force_reveal_draw_damage',
  // Flanking Maneuver: "Teleport any number of allies at one location to
  // another location a chess knight's move away. Draw a card." Two-stage
  // cell pick driven by ops/flankingManeuver.js: stage 1 picks a SOURCE
  // cell holding allied force-movable units (sourceCellsWithAllies),
  // stage 2 picks a knight-move destination (knightMoveCells). Every
  // allied unit on the source cell teleports together to the destination
  // through the synced _moveCardToCell primitive, then the chained `draw`
  // followup fires. args: (none). Followup draw rides the entry.
  GROUP_TELEPORT_KNIGHT: 'group_teleport_knight',
  // Howl from Beyond: "Deal out your top spells, one to each void in the
  // outer columns. Draw any minions with Voidwalk or Monsters from among
  // them and banish the rest." No pick. ops/howlFromBeyond.js enumerates
  // the outer-column voids (outerColumnVoidCells); the runtime reads that
  // many cards off the spellbook top, classifies each (classifyDealtCard:
  // Minion-with-Voidwalk OR Monster ⇒ draw, else banish), then routes the
  // keepers through the synced take-to-hand primitive and the rest
  // through the synced banish-from-pile primitive. args: (none)
  DEAL_TO_OUTER_VOIDS_DRAW_VOIDWALK: 'deal_to_outer_voids_draw_voidwalk',
  // Sir Perceval Genesis: "An opponent chooses a site." Opens an
  // OPPONENT cell-pick over every cell holding a Site; the chosen
  // { col, row } is stamped on the source as `_percevalTargetCell`.
  // Silent. No confirm for the source's controller (the opponent picks).
  // args: (none)
  OPEN_OPPONENT_SITE_CELL_PICK: 'open_opponent_site_cell_pick',
  // Sir Perceval on_stop: gated by `source_at_remembered_cell`. When he
  // stops on the cell the opponent chose, draw a card then reactivate
  // his Genesis (re-open the opponent's site pick). The re-arm clears
  // the remembered cell so the gate doesn't instantly re-satisfy.
  // args: { amount? }. Cards to draw (default 1)
  PERCEVAL_DRAW_REARM: 'perceval_draw_rearm',
  // Kiss of Judas: "This turn, attacks against target Avatar can't be
  // defended and you draw a card whenever they are attacked." A target
  // op (filter: { type: 'Avatar' }); marks the picked Avatar with the
  // `judas_marked` status for the turn and stamps the caster seat so the
  // on_attack hook draws for them. args: (none)
  TAG_AVATAR_UNDEFENDABLE: 'tag_avatar_undefendable',
  // Feast for Crows: "Name a spell. Search an opponent's hand, spellbook
  // and cemetery for cards with that name, and banish them. They
  // shuffle." Fully server-owned (SERVER_CHOICE_OPS): the cast writes a
  // durable modal pendingChoice (promptKind 'name_spell') whose choices
  // span every spell in the catalog (non-Site, non-Avatar); the caster's
  // client renders it through the searchable pile picker and replies
  // with the picked catalog id. The resume banishes every copy from the
  // opponent's hand, spellbook and cemetery (flipped public. Banished is
  // a public zone) and secureShuffles their spellbook. Only the server
  // can resolve this: the opponent's hidden-zone identities never reach
  // the caster's client. args: (none)
  NAME_SPELL_BANISH_FROM_OPPONENT: 'name_spell_banish_from_opponent',
  REMINDER:               'reminder',              // args: { text }. UI hint only
});

export const DURATION = Object.freeze({
  PERMANENT:       'permanent',
  THIS_TURN:       'this_turn',
  // "until your next turn." Expires at the start of the controller's
  // next turn (i.e. at the end of the opponent's intervening turn).
  UNTIL_NEXT_TURN: 'until_next_turn',
  UNTIL_LEAVES:    'until_leaves',
  INSTANT:         'instant',
});

export const SCOPE_WHERE = Object.freeze({
  SELF_CONTROLLER: 'self_controller', // the player who controls the source card
  SELF:            'self',            // the source card itself
  SELF_BEARER:     'self_bearer',
  HERE:            'here',
  NEARBY:          'nearby',
  ADJACENT:        'adjacent',
  GLOBAL:          'global',
  // 'covered'. Cells the aura source covers per its placement shape.
  //   INTERSECTION_4: the 4 cells around its corner anchor.
  //   ATOP_SITE:      the 1 cell it sits on.
  //   INTERSECTION_2: the 2 cells flanking its edge.
  // Only valid for aura sources; resolved via card._auraShape +
  // card._auraAnchor (stamped at addCardToTable). Maps to Sorcery's
  // "affected sites" wording.
  COVERED:         'covered',
});

const TRIGGERS  = new Set(Object.values(TRIGGER));
const OPS       = new Set(Object.values(OP));
const DURATIONS = new Set(Object.values(DURATION));
const SCOPES    = new Set(Object.values(SCOPE_WHERE));

// ── Addressing model (2026-07-26, docs/plans/2026-07-26-automation-addressing-model.md) ──
// The desugar table: authorable verb × select.mode → the internal per-mode op
// + which legacy arg key carries the selector filter. `null` selKey = the mode
// needs no selector blob (watch/self take the addressee from context).
const DESUGAR = {
  [OP.FORCE_REGION]: {
    pick: [OP.FORCE_REGION_TARGET, 'target'],
  },
  [OP.DAMAGE]: {
    pick: [OP.DAMAGE_TARGET, 'target'],
    all: [OP.DAMAGE_EACH, 'each'],
    random: [OP.DAMAGE_RANDOM_IN_SCOPE, 'each'],
    watch: [OP.DAMAGE_WATCH_TARGET, null],
    at_location: [OP.DAMAGE_EACH_AT_LOCATION, 'target'],
  },
  [OP.KILL]: {
    pick: [OP.KILL_TARGET, 'target'],
    all: [OP.KILL_EACH, 'each'],
  },
  [OP.TELEPORT]: {
    pick: [OP.TELEPORT_TARGET, 'target'],
    all: [OP.TELEPORT_EACH_TO_SOURCE, 'filter'],
  },
  [OP.RETURN_TO_HAND]: {
    pick: [OP.RETURN_TO_HAND_TARGET, 'target'],
    all: [OP.RETURN_EACH_TO_HAND, 'each'],
    self: [OP.RETURN_SELF_TO_HAND, null],
  },
  [OP.BANISH]: {
    pick: [OP.BANISH_TARGET, 'target'],
    all: [OP.BANISH_EACH, 'each'],
    self: [OP.BANISH_SELF, null],
  },
  [OP.GRANT_STATUS]: {
    pick: [OP.GRANT_STATUS_TO_TARGET, 'target'],
    all: [OP.GRANT_STATUS_EACH, 'filter'],
    watch: [OP.GRANT_KEYWORD_TO_WATCH_TARGET, null],
  },
};

// Ops the sidecar may NOT use: desugar targets (author the verb + select form
// instead) plus the cell-pick resume codes the runtime synthesizes and no
// entry ever authors (per-op proofs in
// docs/plans/2026-07-26-automation-op-consolidation.md). Dormant-but-intended
// menu vocabulary (the replacement ops, pull/push, heal_player, ...) stays
// AUTHORABLE on purpose. REFERENCE.md is the menu, this set is the ban list.
export const INTERNAL_ONLY_OPS = new Set([
  ...Object.values(DESUGAR).flatMap((modes) => Object.values(modes).map(([op]) => op)),
  OP.PERSECUTOR_STEP, OP.RANGED_SHOT, OP.TAP_PICKED_TIED_MINION,
]);

export const AUTHORABLE_OPS = new Set([...OPS].filter((op) => !INTERNAL_ONLY_OPS.has(op)));

/**
 * Expand an authorable addressed-verb effect into the internal per-mode op
 * the dispatch pipelines understand. Non-addressed effects pass through
 * untouched. Pure. Returns a new object, never mutates.
 */
export function desugarEffect(eff) {
  // Granted-trigger installs nest a whole entry inside args. Recurse so an
  // addressed verb inside a trigger_entry blob desugars like any other.
  if (eff?.args?.trigger_entry) {
    return { ...eff, args: { ...eff.args, trigger_entry: desugarAutomationEntry(eff.args.trigger_entry) } };
  }
  const modes = eff?.op ? DESUGAR[eff.op] : null;
  if (!modes) return eff;
  const { select, ...rest } = eff.args || {};
  const [op, selKey] = modes[select?.mode] || [];
  if (!op) return eff; // invalid mode. Validator rejects it upstream
  const { mode, location, ...filterFields } = select;
  const args = { ...rest };
  if (location !== undefined) args.location = location;
  if (selKey) args[selKey] = filterFields;
  return { ...eff, op, args };
}

export function desugarAutomationEntry(entry) {
  if (!entry?.effect?.some((e) => DESUGAR[e?.op] || e?.args?.trigger_entry)) return entry;
  return { ...entry, effect: entry.effect.map(desugarEffect) };
}

export function desugarAutomationEntries(entries) {
  return Array.isArray(entries) ? entries.map(desugarAutomationEntry) : entries;
}

export function isValidAutomationEntry(entry) {
  if (!entry || typeof entry !== 'object') return false;
  if (!TRIGGERS.has(entry.trigger)) return false;
  if (!Array.isArray(entry.cost)) return false;
  if (!Array.isArray(entry.effect) || entry.effect.length === 0) return false;
  if (!entry.effect.every((e) => OPS.has(e.op))) return false;
  // Addressed verbs must carry a select with a mode the verb supports.
  if (!entry.effect.every((e) => !DESUGAR[e.op] || !!DESUGAR[e.op][e.args?.select?.mode])) return false;
  if (!DURATIONS.has(entry.duration)) return false;
  if (!entry.scope || !SCOPES.has(entry.scope.where)) return false;
  return true;
}
